/**
 * CONSAI Business App - Onboarding Tour Component
 * Forces new users to complete one action before accessing the app
 * Custom implementation without react-joyride for React 19 compatibility
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { 
  ShoppingCart, Package, CreditCard, Receipt, 
  CheckCircle2, Sparkles, ArrowRight, X
} from 'lucide-react';
import axios from 'axios';
import { trackSignup } from '../utils/metaPixel';

const ONBOARDING_KEY = 'consai_onboarding'; // legacy key (for cleanup only)
const getOnboardingKey = (userId) => `consai_onboarding_${userId}`; // user-specific key
const CR_FIRED_KEY = 'consai_cr_fired';
const CR_START_TIME_KEY = 'cr_start_time';
const CR_WINDOW_MS = 10000;
const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingTour = () => {
  const { language } = useLanguage();
  const { user, isAuthenticated, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [showWelcome, setShowWelcome] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  // null = not yet checked, true = has entries, false = no entries
  const [hasEntries, setHasEntries] = useState(null);
  const tooltipRef = useRef(null);
  const selectedActionRef = useRef(null);
  const hasStartedOnboarding = useRef(false);
  const hasCheckedEntries = useRef(false);
  const crTimerRef = useRef(null);
  const prevUserIdRef = useRef(null);

  // Reset entry-check state when the logged-in user changes (prevents stale data across accounts)
  useEffect(() => {
    const newId = user?.user_id || null;
    if (newId !== prevUserIdRef.current) {
      prevUserIdRef.current = newId;
      hasCheckedEntries.current = false;
      setHasEntries(null);
    }
  }, [user?.user_id]);

  // Fire CompleteRegistration once — deduped via localStorage
  const fireCompleteRegistration = (value) => {
    if (localStorage.getItem(CR_FIRED_KEY)) return; // already fired for this user/device
    localStorage.setItem(CR_FIRED_KEY, 'true');
    localStorage.removeItem(CR_START_TIME_KEY);
    sessionStorage.removeItem('cr_pending');
    if (crTimerRef.current) {
      clearTimeout(crTimerRef.current);
      crTimerRef.current = null;
    }
    // Notify PremiumTrialNudgePopup that the onboarding window is done
    window.dispatchEvent(new Event('consai-onboarding-done'));
    trackSignup(value);
  };

  // CR 10-second intent timer — resumes from stored start time on refresh/remount
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (!sessionStorage.getItem('cr_pending')) return;
    if (localStorage.getItem(CR_FIRED_KEY)) {
      sessionStorage.removeItem('cr_pending');
      localStorage.removeItem(CR_START_TIME_KEY);
      // Still notify nudge popup so it can start (CR was fired for a prev account on this device)
      window.dispatchEvent(new Event('consai-onboarding-done'));
      return;
    }

    // Determine remaining time — resume if timer was already running before a refresh
    const storedStart = localStorage.getItem(CR_START_TIME_KEY);
    let delay;
    if (storedStart) {
      const elapsed = Date.now() - parseInt(storedStart, 10);
      delay = Math.max(0, CR_WINDOW_MS - elapsed);
    } else {
      // First time this window starts — record the start timestamp
      localStorage.setItem(CR_START_TIME_KEY, Date.now().toString());
      delay = CR_WINDOW_MS;
    }

    if (delay === 0) {
      // Window already expired (e.g. refresh after 10s) — fire immediately
      fireCompleteRegistration(0);
      return;
    }

    crTimerRef.current = setTimeout(() => {
      fireCompleteRegistration(0); // timeout → low intent
    }, delay);

    return () => {
      if (crTimerRef.current) clearTimeout(crTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  // Beforeunload guard — fires fbq() directly (synchronous, no retry) on tab close / refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!sessionStorage.getItem('cr_pending')) return;
      if (localStorage.getItem(CR_FIRED_KEY)) return;

      // Mark fired so no duplicate can happen on next page load
      localStorage.setItem(CR_FIRED_KEY, 'true');
      localStorage.removeItem(CR_START_TIME_KEY);

      // Call fbq directly — bypass retry mechanism (setTimeout won't run after unload)
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          value: 0,
          currency: 'INR',
          content_name: 'User Signup'
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []); // register once — reads storage fresh inside handler

  // Check if user has any recorded entries (determines if tour is relevant)
  useEffect(() => {
    if (!isAuthenticated || !user || hasCheckedEntries.current) return;
    hasCheckedEntries.current = true;

    axios.get(`${API_URL}/api/onboarding/has-entries`, { withCredentials: true })
      .then(res => {
        setHasEntries(res.data.has_entries);
        // If user has entries, tour is irrelevant — nudge popup can start immediately
        if (res.data.has_entries) {
          window.dispatchEvent(new Event('consai-onboarding-done'));
        }
      })
      .catch(() => setHasEntries(false));
  }, [isAuthenticated, user]);

  // Also dispatch done event if user already completed the tour (returning user)
  useEffect(() => {
    if (user?.onboarding_completed === true) {
      window.dispatchEvent(new Event('consai-onboarding-done'));
    }
  }, [user]);

  // Check if onboarding is completed — backend field is source of truth
  const getOnboardingStatus = () => {
    if (!user?.user_id) return null;
    try {
      const stored = localStorage.getItem(getOnboardingKey(user.user_id));
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return null;
  };

  // Completed = backend flag only (never polluted by another account's localStorage)
  const isOnboardingComplete = () => user?.onboarding_completed === true;

  // Skipped = user-specific key so different accounts don't share skip state
  const isOnboardingSkipped = () => getOnboardingStatus()?.skipped === true;

  // Track onboarding start in backend
  const trackOnboardingStart = async (choice) => {
    if (hasStartedOnboarding.current) return;
    hasStartedOnboarding.current = true;
    
    try {
      await axios.post(`${API_URL}/api/onboarding/start`, {
        onboarding_choice: choice
      }, { withCredentials: true });
    } catch (e) {
      console.error('Failed to track onboarding start:', e);
    }
  };

  // Track onboarding completion in backend
  const trackOnboardingComplete = async (actionType) => {
    try {
      await axios.post(`${API_URL}/api/onboarding/complete`, {
        first_action: actionType
      }, { withCredentials: true });
    } catch (e) {
      console.error('Failed to track onboarding complete:', e);
    }
  };

  // Track onboarding skip in backend
  const trackOnboardingSkip = async () => {
    try {
      await axios.post(`${API_URL}/api/onboarding/skip`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Failed to track onboarding skip:', e);
    }
  };

  // Mark onboarding as complete
  const completeOnboarding = async (actionType) => {
    const data = {
      onboarding_completed: true,
      first_action: actionType,
      completed_at: new Date().toISOString().split('T')[0]
    };
    if (user?.user_id) {
      localStorage.setItem(getOnboardingKey(user.user_id), JSON.stringify(data));
    }
    
    // Update local auth state immediately so tour won't re-trigger before page refresh
    setUser(prev => prev ? { ...prev, onboarding_completed: true } : prev);
    
    // High-intent: user performed an onboarding action within 10s
    fireCompleteRegistration(20);
    
    // Track in backend
    await trackOnboardingComplete(actionType);
    
    setShowSuccess(true);
    setShowTooltip(false);
    
    // Remove pulse effect
    const action = actionCards.find(a => a.id === actionType);
    if (action) {
      const targetElement = document.querySelector(action.targetSelector);
      if (targetElement) {
        targetElement.classList.remove('animate-pulse', 'ring-4', 'ring-[#d7263d]', 'ring-opacity-50');
      }
    }
  };

  // Handle skip
  const handleSkip = async () => {
    const data = {
      onboarding_completed: false,
      skipped: true,
      skipped_at: new Date().toISOString()
    };
    if (user?.user_id) {
      localStorage.setItem(getOnboardingKey(user.user_id), JSON.stringify(data));
    }
    
    // Low-intent: user skipped the tour — also signals nudge popup can now show
    fireCompleteRegistration(0);
    window.dispatchEvent(new Event('consai-onboarding-done'));
    
    // Track in backend
    await trackOnboardingSkip();
    
    setShowWelcome(false);
    navigate('/dashboard');
  };

  // Check if user needs onboarding on login
  useEffect(() => {
    // Skip if action is already selected (user has clicked a card)
    if (selectedAction) return;
    // Wait until entry check completes — null means still loading
    if (hasEntries === null) return;
    
    if (isAuthenticated && user && hasEntries === false && !isOnboardingComplete() && !isOnboardingSkipped()) {
      // Only show on dashboard/main pages, not on login/signup
      const excludedPaths = ['/login', '/signup', '/admin', '/forgot-password', '/auth/callback'];
      const isExcludedPath = excludedPaths.some(path => location.pathname.startsWith(path));
      
      if (!isExcludedPath && !showWelcome) {
        setShowWelcome(true);
      }
    }
  }, [isAuthenticated, user, hasEntries, location.pathname, selectedAction, showWelcome]);

  // Check for immediate show after signup (via URL param or session flag)
  useEffect(() => {
    // Wait until entry check completes
    if (hasEntries === null) return;

    const urlParams = new URLSearchParams(window.location.search);
    const justSignedUp = urlParams.get('onboarding') === 'true' || sessionStorage.getItem('just_signed_up');
    
    if (justSignedUp && isAuthenticated && user && hasEntries === false && !isOnboardingComplete() && !isOnboardingSkipped()) {
      sessionStorage.removeItem('just_signed_up');
      if (urlParams.has('onboarding')) {
        urlParams.delete('onboarding');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
      setShowWelcome(true);
    }
  }, [isAuthenticated, user, hasEntries]);

  // Action cards configuration
  const actionCards = [
    {
      id: 'sale',
      icon: ShoppingCart,
      title: language === 'hi' ? 'पहली बिक्री दर्ज करें' : 'Record First Sale',
      description: language === 'hi' ? 'अपनी पहली बिक्री रिकॉर्ड करें' : 'Record your first sale entry',
      path: '/sales',
      targetSelector: '[data-testid="add-sale-btn"]',
      color: 'bg-green-500',
      bgLight: 'bg-green-50',
      borderColor: 'border-green-200',
      tooltipText: language === 'hi' 
        ? 'इस बटन पर क्लिक करें नई बिक्री जोड़ने के लिए!'
        : 'Click this button to add a new sale!',
    },
    {
      id: 'stock',
      icon: Package,
      title: language === 'hi' ? 'स्टॉक आइटम जोड़ें' : 'Add Stock Item',
      description: language === 'hi' ? 'अपनी इन्वेंट्री में आइटम जोड़ें' : 'Add an item to your inventory',
      path: '/stock',
      targetSelector: '[data-testid="add-item-btn"]',
      color: 'bg-blue-500',
      bgLight: 'bg-blue-50',
      borderColor: 'border-blue-200',
      tooltipText: language === 'hi'
        ? 'इस बटन पर क्लिक करें नया स्टॉक जोड़ने के लिए!'
        : 'Click this button to add a new stock item!',
    },
    {
      id: 'credit',
      icon: CreditCard,
      title: language === 'hi' ? 'क्रेडिट एंट्री जोड़ें' : 'Add Credit Entry',
      description: language === 'hi' ? 'उधार रिकॉर्ड करें' : 'Record a credit transaction',
      path: '/credit',
      targetSelector: '[data-testid="add-credit-btn"]',
      color: 'bg-orange-500',
      bgLight: 'bg-orange-50',
      borderColor: 'border-orange-200',
      tooltipText: language === 'hi'
        ? 'इस बटन पर क्लिक करें नई क्रेडिट एंट्री जोड़ने के लिए!'
        : 'Click this button to add a new credit entry!',
    },
    {
      id: 'invoice',
      icon: Receipt,
      title: language === 'hi' ? 'इनवॉइस बनाएं' : 'Create Invoice',
      description: language === 'hi' ? 'अपना पहला इनवॉइस बनाएं' : 'Create your first invoice',
      path: '/invoices',
      targetSelector: '[data-testid="create-invoice-btn"]',
      color: 'bg-purple-500',
      bgLight: 'bg-purple-50',
      borderColor: 'border-purple-200',
      tooltipText: language === 'hi'
        ? 'इस बटन पर क्लिक करें नया इनवॉइस बनाने के लिए!'
        : 'Click this button to create a new invoice!',
    },
  ];

  // Position the tooltip near the target element with retry logic
  const positionTooltip = (retries = 5) => {
    const currentAction = selectedActionRef.current;
    if (!currentAction) return;
    
    const action = actionCards.find(a => a.id === currentAction);
    if (!action) return;
    
    const targetElement = document.querySelector(action.targetSelector);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 10,
        left: rect.left + (rect.width / 2) - 150, // Center tooltip
      });
      setShowTooltip(true);
      
      // Add pulsing effect to target button
      targetElement.classList.add('animate-pulse', 'ring-4', 'ring-[#d7263d]', 'ring-opacity-50');
    } else if (retries > 0) {
      // Retry if element not found yet
      setTimeout(() => positionTooltip(retries - 1), 300);
    }
  };

  // Handle action card selection
  const handleActionSelect = async (action) => {
    setSelectedAction(action.id);
    selectedActionRef.current = action.id;
    setShowWelcome(false);
    
    // Track onboarding start with choice
    await trackOnboardingStart(action.id);
    
    // Navigate to the selected page
    navigate(action.path);
    
    // Position tooltip after navigation with delay for page load
    setTimeout(() => positionTooltip(5), 1000);
  };

  // Listen for successful form submissions
  useEffect(() => {
    if (!selectedAction || isOnboardingComplete()) return;
    
    // Custom event for onboarding completion
    const handleFormSuccess = () => {
      completeOnboarding(selectedAction);
    };
    
    window.addEventListener('onboarding-entry-created', handleFormSuccess);
    
    return () => {
      window.removeEventListener('onboarding-entry-created', handleFormSuccess);
    };
  }, [selectedAction]);

  // Expose function to trigger completion from other components
  useEffect(() => {
    window.triggerOnboardingComplete = (actionType) => {
      if (!isOnboardingComplete() && selectedAction) {
        completeOnboarding(actionType || selectedAction);
      }
    };
    return () => {
      delete window.triggerOnboardingComplete;
    };
  }, [selectedAction]);

  // Replay tour (for settings)
  const replayTour = () => {
    // Clear user-specific key + legacy key for clean replay
    if (user?.user_id) localStorage.removeItem(getOnboardingKey(user.user_id));
    localStorage.removeItem(ONBOARDING_KEY); // legacy cleanup
    setSelectedAction(null);
    selectedActionRef.current = null;
    hasStartedOnboarding.current = false;
    setShowTooltip(false);
    setShowWelcome(true);
    navigate('/dashboard');
  };

  // Expose replay function globally for Settings page
  useEffect(() => {
    window.replayOnboardingTour = replayTour;
    return () => {
      delete window.replayOnboardingTour;
    };
  }, []);

  // Expose function to show onboarding immediately (called after signup)
  useEffect(() => {
    window.showOnboardingTour = () => {
      if (!isOnboardingComplete() && !isOnboardingSkipped()) {
        setShowWelcome(true);
      }
    };
    return () => {
      delete window.showOnboardingTour;
    };
  }, []);

  // Dismiss tooltip
  const dismissTooltip = () => {
    setShowTooltip(false);
    // Remove pulse effect from target
    const action = actionCards.find(a => a.id === selectedAction);
    if (action) {
      const targetElement = document.querySelector(action.targetSelector);
      if (targetElement) {
        targetElement.classList.remove('animate-pulse', 'ring-4', 'ring-[#d7263d]', 'ring-opacity-50');
      }
    }
  };

  // Close success modal
  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate('/dashboard');
  };

  // Don't render anything if onboarding is complete, skipped, or user has existing entries
  if ((isOnboardingComplete() || isOnboardingSkipped() || hasEntries === true) && !showSuccess) {
    return null;
  }

  const selectedActionData = actionCards.find(a => a.id === selectedAction);

  return (
    <>
      {/* Welcome Modal - Can be dismissed with Skip button */}
      <Dialog open={showWelcome} onOpenChange={() => {}}>
        <DialogContent 
          className="sm:max-w-lg p-0 overflow-hidden [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="bg-gradient-to-br from-[#021945] to-[#0a2d6e] p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="h-8 w-8 text-yellow-400" />
              <DialogTitle className="text-2xl font-bold font-manrope">
                {language === 'hi' ? 'CONSAI में आपका स्वागत है!' : 'Welcome to CONSAI!'}
              </DialogTitle>
            </div>
            <p className="text-slate-300">
              {language === 'hi' 
                ? 'आइए आपकी पहली एंट्री रिकॉर्ड करें। एक विकल्प चुनें:'
                : "Let's record your first entry. Choose an action:"}
            </p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              {actionCards.map((action) => {
                const Icon = action.icon;
                return (
                  <Card 
                    key={action.id}
                    className={`cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${action.bgLight} ${action.borderColor} border-2`}
                    onClick={() => handleActionSelect(action)}
                    data-testid={`onboarding-action-${action.id}`}
                  >
                    <CardContent className="p-4 text-center">
                      <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-slate-900 font-manrope text-sm mb-1">
                        {action.title}
                      </h3>
                      <p className="text-xs text-slate-500">
                        {action.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <p className="text-center text-xs text-slate-400 mt-4">
              {language === 'hi' 
                ? 'एक एक्शन पूरा करने के बाद आप ऐप का पूरा उपयोग कर सकते हैं'
                : 'Complete one action to unlock full app access'}
            </p>
            
            {/* Skip button */}
            <div className="mt-4 text-center">
              <button
                onClick={handleSkip}
                className="text-sm text-slate-400 hover:text-slate-600 underline underline-offset-2"
                data-testid="skip-onboarding-btn"
              >
                {language === 'hi' ? 'अभी के लिए छोड़ें' : 'Skip for now'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tooltip pointing to the target button */}
      {showTooltip && selectedActionData && (
        <>
          {/* Overlay - uses pointer-events-none so user can click through */}
          <div 
            className="fixed inset-0 bg-black/40 z-[9998] pointer-events-none"
          />
          
          {/* Tooltip */}
          <div 
            ref={tooltipRef}
            className="fixed z-[9999] bg-white rounded-xl shadow-2xl p-4 max-w-[300px] border-2 border-[#021945]"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${Math.max(16, Math.min(tooltipPosition.left, window.innerWidth - 316))}px`,
            }}
          >
            {/* Arrow pointing up */}
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-l-2 border-t-2 border-[#021945] rotate-45" />
            
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 ${selectedActionData.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <selectedActionData.icon className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 font-manrope text-sm mb-1">
                  {language === 'hi' ? 'चरण 1' : 'Step 1'}
                </p>
                <p className="text-sm text-slate-600">
                  {selectedActionData.tooltipText}
                </p>
              </div>
              <button 
                onClick={dismissTooltip}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-400">
                {language === 'hi' 
                  ? 'एंट्री सबमिट करने के बाद टूर पूरा हो जाएगा'
                  : 'Tour will complete after you submit an entry'}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Success Celebration Modal */}
      <Dialog open={showSuccess} onOpenChange={handleSuccessClose}>
        <DialogContent className="sm:max-w-md text-center">
          <div className="py-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 font-manrope mb-2">
              {language === 'hi' ? 'बधाई हो!' : 'Perfect!'}
            </h2>
            <p className="text-slate-600 mb-6">
              {language === 'hi' 
                ? `आपने अपनी पहली ${selectedAction === 'sale' ? 'बिक्री' : selectedAction === 'stock' ? 'स्टॉक एंट्री' : selectedAction === 'credit' ? 'क्रेडिट एंट्री' : 'इनवॉइस'} रिकॉर्ड कर ली है!`
                : `You've recorded your first ${selectedAction}!`}
            </p>
            <Button 
              onClick={handleSuccessClose}
              className="bg-[#021945] hover:bg-[#021945]/90 text-white px-8"
              data-testid="explore-app-btn"
            >
              {language === 'hi' ? 'ऐप एक्सप्लोर करें' : 'Explore App'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OnboardingTour;
